import { useState } from 'react';
import { MAX_NUMBER_RECENT_SEARCHES, FlyerType } from '@/config/constants';
import scrollToTop from '@/utils/scroolToTop';
import { SavedSearch } from '@/types/flyer';
import { TrackingData, useAnalytics } from '@/hooks/useAnalytics';
import { useSnackbar } from '@/design-system';
import FlyersContext from './FlyersContext';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useEffectOnMount } from '@/hooks';
import { flyerboardActiveFiltersVar, flyerboardFlyersAmountVar } from '@/graphql/state';

type FlyersProviderType = {
  children: React.ReactNode;
};

const FlyersProvider: React.FC<FlyersProviderType> = ({ children }) => {
  const [savedSearches, setSavedSearches] = useState<Array<SavedSearch>>([]);
  const [recentSearches, setRecentSearches] = useState<Array<string>>([]);
  const { search } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearchParams = new URLSearchParams(search);
  const activeTab = urlSearchParams.get('tab') || FlyerType.school;
  const isSchoolTabSelected = activeTab === FlyerType.school
  const targetTab = isSchoolTabSelected ? FlyerType.community : FlyerType.school;

  const { track, trackEvent } = useAnalytics();
  const showToast = useSnackbar();
  useEffectOnMount(() => {
    const recentSearches = localStorage.getItem('recentSearches');
    if (recentSearches) {
      setRecentSearches(JSON.parse(recentSearches));
    }
  });

  const toggleSavedSearches = (savedSearch: string) => {
    const search = savedSearches.find(s => s.search === savedSearch);
    if (search) {
      setSavedSearches(savedSearches.filter(s => s.search !== savedSearch));
    } else {
      setSavedSearches([
        ...savedSearches,
        {
          search: savedSearch,
          totalFlyers: 0,
          totalNewFlyers: 0,
        },
      ]);
      showToast({ message: 'Search term saved!' });
    }
  };
  const addRecentSearch = (recentSearch: string) => {
    const index = recentSearches.indexOf(recentSearch);
    if (index === -1) {
      const newRecentSearches = [recentSearch, ...recentSearches].splice(
        0,
        MAX_NUMBER_RECENT_SEARCHES,
      );
      saveRecentSearches(newRecentSearches);
    }
  };
  const removeRecentSearch = (recentSearch: string) => {
    const index = recentSearches.indexOf(recentSearch);
    if (index >= 0) {
      saveRecentSearches(recentSearches.filter(s => s !== recentSearch));
    }
  };

  const saveRecentSearches = (searches: string[]) => {
    localStorage.setItem('recentSearches', JSON.stringify(searches));
    setRecentSearches(searches);
  };

  const onChangeFlyerType = (trackingData?: TrackingData) => {
    const newActiveTab =
      FlyerType.school === activeTab ? FlyerType.community : FlyerType.school;
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', newActiveTab);
    if (localStorage.getItem('flyerboardFilters')) {
      localStorage.removeItem('filtersUpdateAt');
      localStorage.removeItem('flyerboardFilters');
      localStorage.removeItem('activeFlyerboard');
      flyerboardActiveFiltersVar({
        grades_age: [],
        types: [],
      });
      flyerboardFlyersAmountVar(0);
    }
    if (trackingData) {
      trackEvent(trackingData);
    }
    setSearchParams(newSearchParams, { replace: true });
    scrollToTop();
  };

  const contextState = {
    savedSearches,
    setSavedSearches,
    toggleSavedSearches,
    activeTab,
    onChangeFlyerType,
    recentSearches,
    saveRecentSearches,
    addRecentSearch,
    removeRecentSearch,
    isSchoolTabSelected,
    targetTab
  };

  return (
    <FlyersContext.Provider value={contextState}>
      {children}
    </FlyersContext.Provider>
  );
};

export default FlyersProvider;
